import { effect, Injectable, signal } from '@angular/core';
import type { LocalEquipmentModel, LocalTestInstructionModel, PlantModel } from '../models/ultrasonic.models';
import { DEFAULT_EQUIPMENT } from './DEFAULT_EQUIPMENT';
import { DEFAULT_PLANTS } from './DEFAULT_PLANTS';
import { DEFAULT_TEST_INSTRUCTIONS } from './DEFAULT_TEST_INSTRUCTIONS';

const STORAGE_KEY = 'ultrasonic-store-v1';

export const DEFAULT_CALIBRATION_TIMER_MIN = 240; // 4 hours — default calibration validity window

interface PersistedState {
  plants: PlantModel[];
  equipment: LocalEquipmentModel[];
  testInstructions: LocalTestInstructionModel[];
  selectedPlantId: string | null;
}

function loadFromStorage(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      return {
        plants: Array.isArray(parsed.plants) && parsed.plants.length > 0 ? parsed.plants : DEFAULT_PLANTS,
        equipment: Array.isArray(parsed.equipment) && parsed.equipment.length > 0 ? parsed.equipment : DEFAULT_EQUIPMENT,
        testInstructions: Array.isArray(parsed.testInstructions) && parsed.testInstructions.length > 0 ? parsed.testInstructions : DEFAULT_TEST_INSTRUCTIONS,
        selectedPlantId: parsed.selectedPlantId ?? null,
      };
    }
  } catch {
    // Corrupt storage — fall through to defaults
  }
  return {
    plants: DEFAULT_PLANTS,
    equipment: DEFAULT_EQUIPMENT,
    testInstructions: DEFAULT_TEST_INSTRUCTIONS,
    selectedPlantId: null,
  };
}

@Injectable({ providedIn: 'root' })
export class UltrasonicStoreService {
  private readonly initial = loadFromStorage();

  readonly plants = signal<PlantModel[]>(this.initial.plants);
  readonly equipment = signal<LocalEquipmentModel[]>(this.initial.equipment);
  readonly testInstructions = signal<LocalTestInstructionModel[]>(this.initial.testInstructions);
  readonly selectedPlantId = signal<string | null>(this.initial.selectedPlantId ?? this.initial.plants[0]?.id ?? null);

  constructor() {
    effect(() => {
      const state: PersistedState = {
        plants: this.plants(),
        equipment: this.equipment(),
        testInstructions: this.testInstructions(),
        selectedPlantId: this.selectedPlantId(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    });
  }

  setSelectedPlant(plantId: string): void {
    this.selectedPlantId.set(plantId);
  }

  // ─── Plants ───────────────────────────────────────────────────────────────

  addPlant(plant: Omit<PlantModel, 'id'>): void {
    this.plants.update((list) => [...list, { ...plant, id: Date.now().toString() }]);
  }

  updatePlant(id: string, changes: Partial<Omit<PlantModel, 'id'>>): void {
    this.plants.update((list) => list.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  }

  deletePlant(id: string): void {
    this.plants.update((list) => list.filter((p) => p.id !== id));
  }

  // ─── Equipment ────────────────────────────────────────────────────────────

  addEquipment(item: Omit<LocalEquipmentModel, 'id'>): void {
    this.equipment.update((list) => [...list, { ...item, id: Date.now().toString() }]);
  }

  updateEquipment(id: string, changes: Partial<Omit<LocalEquipmentModel, 'id'>>): void {
    this.equipment.update((list) => list.map((e) => (e.id === id ? { ...e, ...changes } : e)));
  }

  deleteEquipment(id: string): void {
    this.equipment.update((list) => list.filter((e) => e.id !== id));
  }

  resetEquipmentCalibration(id: string, calibrationIo: boolean, orderNumber?: number, componentId?: string, measurementId?: string): void {
    this.equipment.update((list) =>
      list.map((e) => {
        if (e.id !== id) {
          return e;
        }
        const timer = e.calibrationTimerMin ?? DEFAULT_CALIBRATION_TIMER_MIN;
        return {
          ...e,
          lastCalibration: new Date().toISOString(),
          calibrationIo,
          remainingTime: String(timer),
          calibrationTimerMin: timer,
          lastCalibrationOrderNumber: orderNumber ?? e.lastCalibrationOrderNumber,
          lastCalibrationComponentId: componentId ?? e.lastCalibrationComponentId,
          lastCalibrationMeasurementId: measurementId ?? e.lastCalibrationMeasurementId,
        };
      })
    );
  }

  // ─── Test Instructions ────────────────────────────────────────────────────

  addTestInstruction(item: Omit<LocalTestInstructionModel, 'id'>): void {
    this.testInstructions.update((list) => [...list, { ...item, id: Date.now().toString() }]);
  }

  updateTestInstruction(id: string, changes: Partial<Omit<LocalTestInstructionModel, 'id'>>): void {
    this.testInstructions.update((list) => list.map((t) => (t.id === id ? { ...t, ...changes } : t)));
  }

  deleteTestInstruction(id: string): void {
    this.testInstructions.update((list) => list.filter((t) => t.id !== id));
  }
}
